import { useState, useMemo } from "react";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AbcPetRow } from "@/hooks/useRevenueReport";
import { exportToExcel } from "@/lib/utils/export";

const PAGE_SIZE = 20;

interface RevenueAbcCurveProps {
  data: AbcPetRow[];
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function normalizeSearch(v: string) {
  return v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

const ABC_COLORS: Record<"A" | "B" | "C", { bg: string; letter: string; badge: string }> = {
  A: {
    bg: "bg-orange-50 border-orange-200",
    letter: "bg-orange-100 text-orange-800",
    badge: "bg-orange-100 text-orange-800",
  },
  B: {
    bg: "bg-amber-50 border-amber-200",
    letter: "bg-amber-100 text-amber-800",
    badge: "bg-amber-100 text-amber-800",
  },
  C: {
    bg: "bg-muted border",
    letter: "bg-muted text-muted-foreground",
    badge: "bg-muted text-muted-foreground",
  },
};

export function RevenueAbcCurve({ data }: RevenueAbcCurveProps) {
  const { t } = useTranslation("reports");
  const [search, setSearch] = useState("");
  const [abcFilter, setAbcFilter] = useState<"all" | "A" | "B" | "C">("all");
  const [speciesFilter, setSpeciesFilter] = useState("all");
  const [page, setPage] = useState(1);

  const allSpecies = useMemo(() => {
    const s = new Set(data.map((d) => d.speciesName).filter(Boolean));
    return Array.from(s).sort();
  }, [data]);

  const filtered = useMemo(() => {
    const q = normalizeSearch(search);
    return data.filter((row) => {
      if (abcFilter !== "all" && row.abc !== abcFilter) return false;
      if (speciesFilter !== "all" && row.speciesName !== speciesFilter) return false;
      if (q && !normalizeSearch(row.petName).includes(q) && !normalizeSearch(row.ownerName).includes(q))
        return false;
      return true;
    });
  }, [data, search, abcFilter, speciesFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function handleFilterChange(fn: () => void) {
    fn();
    setPage(1);
  }

  const buckets = useMemo(
    () =>
      (["A", "B", "C"] as const).map((abc) => {
        const rows = data.filter((r) => r.abc === abc);
        return { abc, count: rows.length, revenue: rows.reduce((s, r) => s + r.totalRevenue, 0) };
      }),
    [data]
  );

  function handleExport() {
    exportToExcel({
      rows: filtered.map((r) => ({
        [t("abcPet")]: r.petName,
        [t("abcOwner")]: r.ownerName,
        [t("abcSpecies")]: r.speciesName,
        [t("abcRevenue")]: r.totalRevenue,
        [t("abcClass")]: r.abc,
      })),
      sheetName: t("abcTitle"),
      fileName: "curva-abc-pets.xlsx",
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t("abcTitle")}</CardTitle>
            <CardDescription>{t("abcDesc")}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={data.length === 0}>
            <Download className="h-4 w-4 mr-1" />
            {t("export")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* Bucket summary — single row */}
        <div className="grid grid-cols-3 gap-3">
          {buckets.map(({ abc, count, revenue }) => (
            <div
              key={abc}
              className={`rounded-xl border p-4 flex items-center gap-3 ${ABC_COLORS[abc].bg}`}
            >
              <div
                className={`h-10 w-10 rounded-xl flex items-center justify-center text-xl font-extrabold shrink-0 ${ABC_COLORS[abc].letter}`}
              >
                {abc}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{t(`abcBucket${abc}Label`)}</p>
                <p className="text-xs text-muted-foreground">{t("abcPetCount", { count })}</p>
                <p className="font-extrabold text-base tracking-tight mt-0.5">
                  {formatCurrency(revenue)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={t("abcSearchPlaceholder")}
            value={search}
            onChange={(e) => handleFilterChange(() => setSearch(e.target.value))}
            className="text-xs w-48"
            inputSize="sm"
          />
          <Select
            value={abcFilter}
            onValueChange={(v) => handleFilterChange(() => setAbcFilter(v as "all" | "A" | "B" | "C"))}
          >
            <SelectTrigger className="h-8 text-xs w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filterAll")}</SelectItem>
              <SelectItem value="A">{t("abcClassA")}</SelectItem>
              <SelectItem value="B">{t("abcClassB")}</SelectItem>
              <SelectItem value="C">{t("abcClassC")}</SelectItem>
            </SelectContent>
          </Select>
          {allSpecies.length > 0 && (
            <Select
              value={speciesFilter}
              onValueChange={(v) => handleFilterChange(() => setSpeciesFilter(v))}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("filterAllSpecies")}</SelectItem>
                {allSpecies.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t("emptyBreakdown")}</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>{t("abcPet")}</TableHead>
                    <TableHead>{t("abcOwner")}</TableHead>
                    <TableHead className="text-right">{t("abcRevenue")}</TableHead>
                    <TableHead className="text-center">{t("abcClass")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageItems.map((row, idx) => (
                    <TableRow key={row.petId}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {(currentPage - 1) * PAGE_SIZE + idx + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold shrink-0">
                            {row.petName[0]?.toUpperCase() ?? "?"}
                          </div>
                          <div>
                            <p className="font-semibold text-sm leading-none">{row.petName}</p>
                            {row.speciesName && (
                              <p className="text-xs text-muted-foreground mt-0.5">{row.speciesName}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{row.ownerName}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(row.totalRevenue)}
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={`text-xs font-bold px-2 py-0.5 rounded-full ${ABC_COLORS[row.abc].badge}`}
                        >
                          {row.abc}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t("abcPetCount", { count: filtered.length })}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={currentPage === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 tabular-nums">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={currentPage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
